// Windows ships no country flag glyphs, so 🇬🇧 renders as the letters "GB".
// When flags are unsupported, we add a flags-only @font-face (Twemoji Country Flags,
// limited by unicode-range) under the family name of every loaded web font, mirroring
// each face's weight/style/stretch. The browser then draws flag characters from it
// while all other text keeps the original font, with no per-component changes.
// Detection is adapted from country-flag-emoji-polyfill (MIT).

const FLAG_FONT_URL = '/fonts/TwemojiCountryFlags.woff2'
const FLAG_UNICODE_RANGE =
  'U+1F1E6-1F1FF, U+1F3F4, U+E0062-E0063, U+E0065, U+E0067, U+E006C, U+E006E, U+E0073-E0074, U+E0077, U+E007F'
const EMOJI_FONT_STACK =
  '"Twemoji Mozilla","Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol","Noto Color Emoji","EmojiOne Color","Android Emoji",sans-serif'

// A color emoji glyph renders identically whatever the fillStyle; letters do not.
const supportsEmoji = (text: string) => {
  const canvas = document.createElement('canvas')
  canvas.width = 1
  canvas.height = 1
  const ctx = canvas.getContext('2d', { willReadFrequently: true })
  if (!ctx) return true
  ctx.textBaseline = 'top'
  ctx.font = `100px ${EMOJI_FONT_STACK}`
  ctx.scale(0.01, 0.01)
  const draw = (color: string) => {
    ctx.clearRect(0, 0, 100, 100)
    ctx.fillStyle = color
    ctx.fillText(text, 0, 0)
    return ctx.getImageData(0, 0, 1, 1).data.join(',')
  }
  const white = draw('#fff')
  const black = draw('#000')
  return white === black && !black.startsWith('0,0,0,')
}

export const needsCountryFlagPolyfill = () =>
  supportsEmoji('\u{1F60A}') && !supportsEmoji('\u{1F1E8}\u{1F1ED}')

export const applyCountryFlagPolyfill = () => {
  const style = document.createElement('style')
  style.setAttribute('data-country-flag-polyfill', '')
  document.head.appendChild(style)
  const mirrored = new Set<string>()

  const mirrorLoadedFaces = () => {
    const rules: string[] = []
    document.fonts.forEach((face) => {
      const family = face.family.replace(/^["']|["']$/g, '')
      const key = [family, face.weight, face.style, face.stretch].join('|')
      if (mirrored.has(key)) return
      mirrored.add(key)
      const descriptors = [
        `font-family: "${family.replace(/"/g, '\\"')}"`,
        `src: url('${FLAG_FONT_URL}') format('woff2')`,
        `unicode-range: ${FLAG_UNICODE_RANGE}`,
        `font-weight: ${face.weight}`,
        `font-style: ${face.style}`,
        `font-stretch: ${face.stretch}`,
        'font-display: swap',
      ]
      rules.push(`@font-face { ${descriptors.join('; ')} }`)
    })
    if (rules.length) style.textContent += rules.join('\n')
  }

  mirrorLoadedFaces()
  document.fonts.addEventListener('loadingdone', mirrorLoadedFaces)
  window.addEventListener('load', mirrorLoadedFaces)
  return () => {
    document.fonts.removeEventListener('loadingdone', mirrorLoadedFaces)
    window.removeEventListener('load', mirrorLoadedFaces)
    style.remove()
  }
}
